import React, { useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  Paper, Grid, Button, Select, FormControl, MenuItem, Input
} from '@material-ui/core'

import { makeStyles } from '@material-ui/core/styles'

import { NotificationToast } from '../../tools/Toast'

import { updateOrganizationalContacts } from '../../../../redux/actions'

const useStyles = makeStyles((theme) => ({
  blueBtn: {
    backgroundColor: '#3984f7',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#0651C4'
    }
  },
  underline: {
    '&&&&:hover:before': {
      borderBottomColor: '#8AB651'
    },
    '&:after': {
      borderBottomColor: '#A4D164'
    }
  }
}))

const useSelectStyles = makeStyles((theme) => ({
  select: {
    overflow: 'hidden',
    backgroundColor: '#fff',
    width: '100%',
    padding: theme.spacing(1.5, 2),
    border: '1px solid rgba(0, 0, 0, 0.26)',
    borderTopLeftRadius: '4px',
    borderTopRightRadius: '4px',
    transition: 'background-color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
    '&:focus': {
      backgroundColor: '#f5f5f5',
      borderTopLeftRadius: '4px',
      borderTopRightRadius: '4px'
    },
    '&:hover': {
      backgroundColor: '#f5f5f5'
    }
  }
}))

/**
 * Returns organization contacts forms
 */
const OrganizationContacts = ({ onSubmit }) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const selectClasses = useSelectStyles()

  const availableContacts = useSelector(state => state.orgContacts.availableContacts)
  const accountAdministrator = useSelector(state => state.orgContacts.accountAdministrator)
  const billingContact = useSelector(state => state.orgContacts.billingContact)
  const technicalContact = useSelector(state => state.orgContacts.technicalContact)
  const accountId = useSelector(state => state.auth.accountId)

  const [options, setOptions] = useState([])
  const [selectedAA, setSelectedAA] = useState('DEFAULT')
  const [selectedBC, setSelectedBC] = useState('DEFAULT')
  const [selectedTC, setSelectedTC] = useState('DEFAULT')

  useMemo(() => {
    if (availableContacts.length !== 0) {
      const contactOptions = availableContacts.map((contactInfo) => {
        return ({
          contactID: contactInfo.supervisorID,
          contactName: `${contactInfo.firstName} ${contactInfo.lastName}`
        })
      })
      setOptions(contactOptions)
    }
  }, [availableContacts])

  useEffect(() => {
    if (accountAdministrator && accountAdministrator !== null && accountAdministrator !== '') {
      setSelectedAA(accountAdministrator)
    }
    if (billingContact && billingContact !== null && billingContact !== '') {
      setSelectedBC(billingContact)
    }
    if (technicalContact && technicalContact !== null && technicalContact !== '') {
      setSelectedTC(technicalContact)
    }
  }, [accountAdministrator, billingContact, technicalContact])

  const submitForm = async () => {
    let contactAA = ''
    let contactBC = ''
    let contactTC = ''

    if (selectedAA !== 'DEFAULT') {
      const contactIndexAA = availableContacts.findIndex((contact) => {
        return contact.supervisorID === selectedAA
      })
      contactAA = {
        supervisorID: availableContacts[contactIndexAA].supervisorID,
        accountID: availableContacts[contactIndexAA].accountID
      }
    } else {
      contactAA = {
        supervisorID: 'nullify',
        accountID: accountId
      }
    }

    if (selectedBC !== 'DEFAULT') {
      const contactIndexBC = availableContacts.findIndex((contact) => {
        return contact.supervisorID === selectedBC
      })
      contactBC = {
        supervisorID: availableContacts[contactIndexBC].supervisorID,
        accountID: availableContacts[contactIndexBC].accountID
      }
    } else {
      contactBC = {
        supervisorID: 'nullify',
        accountID: accountId
      }
    }

    if (selectedTC !== 'DEFAULT') {
      const contactIndexTC = availableContacts.findIndex((contact) => {
        return contact.supervisorID === selectedTC
      })
      contactTC = {
        supervisorID: availableContacts[contactIndexTC].supervisorID,
        accountID: availableContacts[contactIndexTC].accountID
      }
    } else {
      contactTC = {
        supervisorID: 'nullify',
        accountID: accountId
      }
    }

    if (contactAA !== '' && contactBC !== '' && contactTC !== '') {
      dispatch(updateOrganizationalContacts(contactAA, contactBC, contactTC, fireSuccess, fireFailure))
    }
  }

  const fireSuccess = () => {
    NotificationToast(false, 'Successfully updated contacts!')
  }

  const fireFailure = () => {
    NotificationToast(true, 'Failed to update contacts')
  }

  return (
    <>

      <Grid container direction='row'>
        <Grid container item direction='column' spacing={2} className='m-auto'>

          <Grid container item direction='row' justify='space-between' alignItems='center'>
            <Grid item>
              <h2>Account Contacts</h2>
            </Grid>

            <Grid item>
              <Button
                variant='contained'
                className={classes.blueBtn}
                onClick={() => submitForm()}
              >
                Save
              </Button>
            </Grid>
          </Grid>

          <Grid container item direction='row' className='pt-0'>
            <Grid container item direction='column' spacing={3} className='m-auto'>

              <Grid container item direction='row' className='px-0'>
                <Paper elevation={2} className='w-100 py-4 px-4'>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={6}>
                      <h5 className='mr-1 mt-2'>Account Administrator:</h5>
                    </Grid>

                    <Grid item xs={6}>
                      <Grid item xs={10}>
                        <FormControl fullWidth>
                          <Select
                            value={selectedAA}
                            placeholder='Select an option'
                            onChange={e => setSelectedAA(e.target.value)}
                            variant='filled'
                            input={<Input classes={{ underline: classes.underline }} />}
                            classes={selectClasses}
                          >
                            <MenuItem value='DEFAULT'>None</MenuItem>
                            {
                              options.map((option) => (
                                <MenuItem key={`contact-${option.contactID}`} value={option.contactID}>
                                  {option.contactName}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid container item direction='row' className='px-0'>
                <Paper elevation={2} className='w-100 py-4 px-4'>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={6}>
                      <h5 className='mr-1 mt-2'>Billing Contact:</h5>
                    </Grid>

                    <Grid item xs={6}>
                      <Grid item xs={10}>
                        <FormControl fullWidth>
                          <Select
                            value={selectedBC}
                            placeholder='Select an option'
                            displayEmpty
                            onChange={e => setSelectedBC(e.target.value)}
                            variant='filled'
                            input={<Input classes={{ underline: classes.underline }} />}
                            classes={selectClasses}
                          >
                            <MenuItem value='DEFAULT'>None</MenuItem>
                            {
                              options.map((option) => (
                                <MenuItem key={`contact-${option.contactID}`} value={option.contactID}>
                                  {option.contactName}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid container item direction='row' className='px-0'>
                <Paper elevation={2} className='w-100 py-4 px-4'>
                  <Grid container direction='row' alignItems='center'>
                    <Grid item xs={6}>
                      <h5 className='mr-1 mt-2'>Technical Contact:</h5>
                    </Grid>

                    <Grid item xs={6}>
                      <Grid item xs={10}>
                        <FormControl fullWidth>
                          <Select
                            value={selectedTC}
                            placeholder='Select an option'
                            displayEmpty
                            onChange={e => setSelectedTC(e.target.value)}
                            variant='filled'
                            input={<Input classes={{ underline: classes.underline }} />}
                            classes={selectClasses}
                          >
                            <MenuItem value='DEFAULT'>None</MenuItem>
                            {
                              options.map((option) => (
                                <MenuItem key={`contact-${option.contactID}`} value={option.contactID}>
                                  {option.contactName}
                                </MenuItem>
                              ))
                            }
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

            </Grid>
          </Grid>

          <Grid container item direction='row' justify='flex-end' alignItems='center'>
            <Grid item>
              <Button
                variant='contained'
                className={classes.blueBtn}
                onClick={() => submitForm()}
              >
                Save
              </Button>
            </Grid>
          </Grid>

        </Grid>
      </Grid>
    </>
  )
}

export default OrganizationContacts
